import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { InfoService } from '@wonder-core-ng/info-store/info.service';
import { selectIsLoggedIn } from '@wonder-core-ng/session-store/session.selectors';
import { selectSelectedTenantId } from '@wonder-core-ng/tenant-store/tenant.selectors';
import { isNotNull } from '@wonder-core-ng/utils';
import {
  EMPTY,
  catchError,
  concatMap,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { NotificationActions } from './notification.actions';
import { NotificationApiService } from './notification.api.service';

/**
 * How often the badge refreshes. Matches the event scheduler's `@Cron` cadence: notifications are
 * written at most once a minute, so polling faster would only add requests.
 */
export const NOTIFICATION_POLL_MS = 60_000;

@Injectable()
export class NotificationEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(NotificationApiService);
  private readonly store = inject(Store);
  private readonly infoService = inject(InfoService);
  private readonly router = inject(Router);

  /**
   * True while the visitor is on a `/session/*` screen (login, recovery, invitation). Nothing is
   * known until the first navigation ends: before that `router.url` is just `/`, not the real page.
   */
  private readonly onSessionPage$ = () =>
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      this.router.navigated ? startWith(this.router.url) : (source) => source,
      map((url) => url.startsWith('/session/')),
      distinctUntilChanged(),
    );

  private readonly tenantId$ = () =>
    this.store.select(selectSelectedTenantId).pipe(filter(isNotNull));

  /**
   * Turns `startPolling` into a load now and every minute after, until `stopPolling`, and only while
   * there is a session.
   *
   * Without a session the request answers 401 and the app's auth interceptor sends the user to the
   * login page, which would make the public screens (password recovery, invitation) unreachable
   * from a link. So the poll follows the session: it starts at login and stops at logout.
   *
   * A stored session is rehydrated from localStorage without being validated, so an expired one
   * counts as "logged in" too. That is why the poll also stays off on `/session/*` routes, where
   * the visitor is by definition not using the app yet.
   *
   * Each run also waits for a tenant: the header bell mounts before the session has resolved which
   * tenant is active, and a request with no `x-tenant-id` is rejected. Waiting here (rather than
   * letting the load effect queue up actions) means no burst of requests the moment login lands.
   */
  pollNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.startPolling),
      switchMap(() =>
        combineLatest([this.store.select(selectIsLoggedIn), this.onSessionPage$()]).pipe(
          map(([loggedIn, onSessionPage]) => loggedIn && !onSessionPage),
          distinctUntilChanged(),
          switchMap((shouldPoll) =>
            shouldPoll
              ? this.tenantId$().pipe(
                  take(1),
                  switchMap(() => timer(0, NOTIFICATION_POLL_MS)),
                  map(() => NotificationActions.loadNotifications()),
                )
              : EMPTY,
          ),
          takeUntil(this.actions$.pipe(ofType(NotificationActions.stopPolling))),
        ),
      ),
    ),
  );

  /**
   * `switchMap`, not `concatMap`: on a poll tick only the freshest answer matters, and letting a slow
   * reply land after a newer one would make the badge count jump backwards.
   *
   * A failure is recorded in state but **not** surfaced as a message. This runs unattended every
   * minute on every screen, so a backend blip would otherwise stack up toasts the user never asked
   * for. The two user-initiated actions below do report their errors.
   */
  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.loadNotifications),
      concatLatestFrom(this.tenantId$),
      switchMap(([, tenantId]) =>
        this.api.loadNotifications(tenantId).pipe(
          map((notifications) => NotificationActions.loadNotificationsSuccess({ notifications })),
          catchError((error: unknown) =>
            of(NotificationActions.loadNotificationsFailure({ error })),
          ),
        ),
      ),
    ),
  );

  markRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.markRead),
      concatLatestFrom(this.tenantId$),
      concatMap(([{ id }, tenantId]) =>
        this.api.markNotificationRead(id, tenantId).pipe(
          map((notification) => NotificationActions.markReadSuccess({ notification })),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$notifications.error-saving',
              severity: 'error',
              error,
            });
            return of(NotificationActions.markReadFailure({ error }));
          }),
        ),
      ),
    ),
  );

  markAllRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.markAllRead),
      concatLatestFrom(this.tenantId$),
      concatMap(([, tenantId]) =>
        this.api.markAllNotificationsRead(tenantId).pipe(
          // The endpoint answers with a count, so the effect stamps the time the reducer will apply.
          map(() => NotificationActions.markAllReadSuccess({ readAt: new Date().toISOString() })),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$notifications.error-saving',
              severity: 'error',
              error,
            });
            return of(NotificationActions.markAllReadFailure({ error }));
          }),
        ),
      ),
    ),
  );
}
